/**
 * Vista previa de los libros fiscales de una compra con factura (pedido del usuario del 2026-10-30: «previsualizar al final
 * para que la dueña vea cómo quedaría el movimiento en los libros fiscales»). Solo muestra: no calcula nada.
 *
 * - **Libro de compras:** la fila que la compra tendrá en el libro del mes de su fecha del período fiscal (o de la fecha de
 *   la factura, si no la tiene). Los Bs son los del cálculo del servidor (`POST /compras/calcular`) o los guardados en el
 *   borrador: los mismos que el libro lee de la compra confirmada (`libroCompras`, `reportes/libros.ts`). Lo retenido
 *   entra al libro del mes en que se numera el comprobante; mientras sea provisional, la fila sale con lo retenido en cero.
 * - **Libro de inventario (real y fiscal):** cada artículo entra en el mes en que se CONFIRMA la compra, con su valor en Bs
 *   de la factura (el `subtotal_bs` del renglón, el que guarda el kardex). Lo ve quien ve costos, como el libro.
 *
 * Prueba: `VistaLibros.test.tsx` (pantalla) y `apps/api/test/bugs/vista-libros-compra.spec.ts` (lo mostrado es lo que
 * el libro dice después de confirmar).
 */
import { diaLegible, formatearTasa, Money, type ComprobanteDTO } from '@mg/shared';
import { AvisoLibro, BsLibro as Bs, FilaLibro as Fila, hoyCaracas, mesDeLaFecha, TarjetaVistaLibros, TituloLibro } from '@/componentes/VistaPreviaLibros.tsx';

export { mesDeLaFecha };

export interface LineaVistaLibros {
  tipo: string;
  producto_id: string | null;
  producto_codigo: string | null;
  descripcion: string;
  cantidad: string;
  subtotal_bs: string;
}

/** Lo que necesita la vista: los datos de la factura y los Bs fiscales del cálculo o del borrador. */
export interface DatosVistaLibros {
  fecha_factura: string;
  fecha_fiscal: string | null;
  proveedor_rif: string;
  proveedor_razon_social: string;
  numero_factura: string;
  numero_control: string | null;
  tasa_bcv: string;
  total_factura_bs: string;
  base_exenta_bs: string;
  base_gravada_bs: string;
  iva_bs: string;
  retencion_iva_bs: string;
  retencion_islr_bs: string;
  lineas: LineaVistaLibros[];
}

/** Compra nueva, borrador (al editarlo o en su detalle) o corrección de una confirmada. */
export type MomentoVistaLibros = 'nueva' | 'borrador' | 'correccion';

export function VistaLibros({ datos, momento, veInventario, comprobantes = [], hoy = hoyCaracas(), testid = 'vista-libros' }: {
  datos: DatosVistaLibros;
  momento: MomentoVistaLibros;
  /** Quien ve costos (dueña, contadora): el libro de inventario lleva costos. */
  veInventario: boolean;
  /** Los comprobantes de la compra (al corregir: si el de IVA ya tiene número, lo conserva). */
  comprobantes?: ComprobanteDTO[] | undefined;
  hoy?: string;
  testid?: string;
}) {
  const fiscal = datos.fecha_fiscal || null;
  const fechaLibro = fiscal ?? datos.fecha_factura;
  const retIva = Money.parse(datos.retencion_iva_bs);
  const retIslr = Money.parse(datos.retencion_islr_bs);
  const retiene = retIva.esPositivo() || retIslr.esPositivo();
  // Al corregir, el comprobante de IVA que ya tenía número lo conserva (y sale como corregido)
  const ivaNumerado = momento === 'correccion' ? comprobantes.find((c) => c.tipo === 'iva' && c.estado === 'vigente' && c.numero)?.numero ?? null : null;
  const articulos = datos.lineas.filter((l) => l.tipo === 'articulo' && l.producto_id);

  return (
    <TarjetaVistaLibros testid={testid}>

      <div className="space-y-0.5" data-testid={`${testid}-compras`}>
        <TituloLibro titulo={`Libro de compras de ${mesDeLaFecha(fechaLibro)}`} testid={`${testid}-mes`} nota={fiscal ? `Entra por la fecha del período fiscal (${diaLegible(fiscal)}).` : `Entra por la fecha de la factura (${diaLegible(datos.fecha_factura)}): no tiene fecha del período fiscal.`} />
        <Fila etiqueta="Fecha">{diaLegible(datos.fecha_factura)}{fiscal && fiscal !== datos.fecha_factura ? <span className="block text-[11px] text-muted-foreground">fiscal {diaLegible(fiscal)}</span> : null}</Fila>
        <Fila etiqueta="RIF">{datos.proveedor_rif}</Fila>
        <Fila etiqueta="Proveedor">{datos.proveedor_razon_social}</Fila>
        <Fila etiqueta="N° factura">{datos.numero_factura}</Fila>
        <Fila etiqueta="N° control" testid={`${testid}-control`}>{datos.numero_control || '—'}</Fila>
        <Fila etiqueta="Total con IVA" testid={`${testid}-total`}><Bs valor={datos.total_factura_bs} /></Fila>
        <Fila etiqueta="Exento" testid={`${testid}-exento`}><Bs valor={datos.base_exenta_bs} /></Fila>
        <Fila etiqueta="Base" testid={`${testid}-base`}><Bs valor={datos.base_gravada_bs} /></Fila>
        <Fila etiqueta="IVA" testid={`${testid}-iva`}><Bs valor={datos.iva_bs} /></Fila>
        <Fila etiqueta="IVA retenido" testid={`${testid}-ret-iva`}><Bs valor={datos.retencion_iva_bs} /></Fila>
        <Fila etiqueta="N° comprobante" testid={`${testid}-comprobante`}>{ivaNumerado ? `${ivaNumerado} (lo conserva)` : retIva.esPositivo() ? 'provisional, se numera después' : '—'}</Fila>
        <Fila etiqueta="ISLR retenido" testid={`${testid}-ret-islr`}><Bs valor={datos.retencion_islr_bs} /></Fila>
        <p className="text-[11px] text-muted-foreground">En Bs, como se guardan con la compra (tasa BCV {formatearTasa(datos.tasa_bcv)}).</p>
        {retiene && !ivaNumerado ? (
          <AvisoLibro testid={`${testid}-aviso-retencion`}>
            Lo retenido entra al libro del mes en que numeres los comprobantes. Hasta entonces la fila sale con la marca «retención provisional» y lo retenido en cero. Si los numeras en otro mes, allá sale como fila «Retención».
          </AvisoLibro>
        ) : null}
      </div>

      {veInventario ? (
        <div className="space-y-0.5 border-t pt-2" data-testid={`${testid}-inventario`}>
          <TituloLibro titulo="Libro de inventario (real y fiscal)" />
          {momento === 'correccion' ? (
            <p className="text-[11px] text-muted-foreground">La corrección suma o resta solo la diferencia de unidades y de Bs contra lo que ya había entrado, en el mes en que la guardes.</p>
          ) : articulos.length === 0 ? (
            <p className="text-[11px] text-muted-foreground">No cambia: la compra solo trae servicios.</p>
          ) : (
            <>
              <p className="text-[11px] text-muted-foreground" data-testid={`${testid}-inventario-mes`}>Entra en el mes en que se confirme la compra (hoy: {mesDeLaFecha(hoy)}), no por la fecha fiscal, con su valor en Bs de la factura. Suma a las entradas por compras de cada producto.</p>
              {articulos.map((l, i) => (
                <p key={`${l.producto_id}-${i}`} className="flex justify-between gap-3" data-testid={`${testid}-entrada-${l.producto_codigo ?? i}`}>
                  <span className="min-w-0 break-words">{l.producto_codigo} · {l.descripcion}</span>
                  <span className="num shrink-0 text-right">+{Money.parse(l.cantidad).toDisplay()} · <Bs valor={l.subtotal_bs} /></span>
                </p>
              ))}
            </>
          )}
        </div>
      ) : null}
    </TarjetaVistaLibros>
  );
}
